<script setup lang="ts">

import AppContainer from '@apparatix/library/AppContainer.vue';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import DataTable, { type DataTableFilterEvent, type DataTablePageEvent, type DataTableRowClickEvent, type DataTableSortEvent } from 'primevue/datatable';
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload';
import HeightOptimizer from '@apparatix/library/HeightOptimizer.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import UserRoleGate from '@apparatix/library/UserRoleGate.vue';
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import { Section } from '@apparatix/library/core';
import Typography from '@apparatix/library/core/typography/Typography.ts';
import { useEndpoint } from '@apparatix/composables';
import {
  useMeasurementsStore,
  type MeasurementRow,
  type ProviderPriorityRow,
  type SelectOption,
} from '@apparatix/stores/useMeasurementsStore';

const measurementsStore = useMeasurementsStore();
const {
  measurements,
  totalRecords,
  providerOptions,
  metricOptions,
  segmentOptions,
  intervalOptions,
  venueOptions,
  adminToolsEnabled,
  selectedProvider,
  selectedMetric,
  selectedSegment,
  selectedInterval,
  rowsPerPage,
  firstRecord,
  tableSortField,
  tableSortOrder,
  tableFilters,
  providerPriority,
  originalProviderOrder,
} = storeToRefs(measurementsStore);

const router = useRouter();
const route = useRoute();

const onRowClick = (event: DataTableRowClickEvent) => {
  const row = event.data as { inv_id: number; inv_number: string };
  const base = (route.meta?.basePath as string) ?? '/measurements';

  router.push({
    path: `${base}/detail/inventory/${row.inv_id}/measurement`,
    query: { invNumber: row.inv_number },
  });
};

const file = ref<File | null>(null);
const message = ref('');
const error = ref('');
const warning = ref('');

const interval = ref('28');
const headerRow = ref(1);
const firstDataRow = ref(2);
const downloadPreUpdateImpressions = ref(true);

// Upload-scoped provider/audience (separate from the table selectors). Defaults
// match the legacy inv_dec behavior (DEC provider, Persons 0+).
const uploadProvider = ref('DEC');
const uploadSegment = ref('P0+');

// Interval the uploaded impressions are reported over (days the figure covers).
const uploadIntervalOptions = [
  { name: '4 Week', value: '28' },
  { name: 'Weekly', value: '7' },
  { name: 'Daily', value: '1' },
];

const impressionsIntervalTooltip = 'Have the impressions been provided as 4-week impressions, weekly impressions, or daily impressions?';
const headerRowTooltip = 'The row containing the headers: Faces, Impressions.';
const firstDataRowTooltip = 'The row containing the first face/impression data.';
const downloadPreUpdateImpressionsTooltip = 'Downloads face impressions from before the update, expressed at the selected interval. To revert an update, upload this file with the same Provider, Audience, and Interval.';

const filterDescriptions = {
  provider:
    'Which data source supplies the value. "Default (calculated)" walks every provider for the selected audience, in priority order (1st, 2nd, 3rd…) and uses the highest-ranked source that has a non-zero value for each data point. If none have a value, it falls back to DEC Persons 0+. Pick a specific provider to see only its values.',
  metric:
    'The kind of measurement shown — e.g. Impressions (eyes on), Reach, Frequency, TRP. Daily Effective Circulation is Impressions supplied by the DEC provider. Reach, Frequency, TRP and Reach % are non-additive and only display at the interval they were measured at.',
  segment:
    'The audience the measurement is counted for (e.g. Persons 0+, 18+, 21+). Defaults to your configured impressions audience.',
  interval:
    'The time window the value is expressed over: Daily, Weekly, or 4 Week. Additive metrics (Impressions, Plays) are scaled from the stored weekly value to the selected interval. Non-additive metrics (Reach, Frequency, TRP, Reach %) can\'t be scaled — they show a value only for faces whose data is stored at the selected interval, and are blank otherwise (including 4 Week, which isn\'t stored).',
};

type PreUpdateImpression = {
  face: string;
  impressions: string | number | null;
};

type UploadMeasurementsResponse = {
  success: boolean;
  message?: string;
  warning?: string;
  validCount?: number;
  invalidCount?: number;
  invalidFaces?: string[];
  skippedRowCount?: number;
  skippedRows?: unknown[];
  preUpdateImpressions?: PreUpdateImpression[];
};

type UploadMeasurementsError = {
  error?: string;
  message?: string;
  errors?: Record<string, string[]>;
};

const formatUploadError = (response?: UploadMeasurementsError) => {
  if (response?.error) {
    return response.error;
  }

  if (response?.errors) {
    return Object.values(response.errors).flat().join('\n');
  }

  if (response?.message) {
    return response.message;
  }

  return 'Upload failed.';
};

const { getResults: uploadMeasurements, isLoading: isUploading } = useEndpoint<UploadMeasurementsResponse, UploadMeasurementsError>({
  type: 'POST',
  url: '/manage-measurements/upload',
  successField: 'success',
  onError: response => {
    error.value = formatUploadError(response);
  },
  toast: { showOnSuccess: false, showOnError: false },
});

const handleFileSelect = (event: FileUploadSelectEvent) => {
  file.value = (event.files as File[])[0] ?? null;
  message.value = '';
  error.value = '';
  warning.value = '';
};

const escapeCsvValue = (value: string | number | null | undefined) => {
  const stringValue = value === null || value === undefined ? '' : String(value);

  if (!(/[",\n\r]/).test(stringValue)) {
    return stringValue;
  }

  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadPreUpdateImpressionsCsv = (rows: PreUpdateImpression[]) => {
  if (rows.length === 0) {
    return;
  }

  const csv = [
    ['Faces', 'Impressions'],
    ...rows.map(row => [row.face, row.impressions]),
  ]
    .map(row => row.map(escapeCsvValue).join(','))
    .join('\n');

  const blob = new Blob([`${csv}\n`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `pre-update-impressions-${filenameTimestamp()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const filenameTimestamp = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');

  return [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
  ].join('-') + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const uploadFile = async () => {
  message.value = '';
  error.value = '';
  warning.value = '';
  if (!file.value || isUploading.value) {
    return;
  }

  const formData = new FormData();
  formData.append('file', file.value);
  formData.append('interval', interval.value);
  formData.append('headerRow', String(headerRow.value));
  formData.append('firstDataRow', String(firstDataRow.value));
  formData.append('downloadPreUpdateImpressions', downloadPreUpdateImpressions.value ? '1' : '0');
  formData.append('provider', uploadProvider.value);
  formData.append('segment', uploadSegment.value);

  const response = await uploadMeasurements<UploadMeasurementsResponse, FormData>(formData);

  if (!response) {
    return;
  }

  message.value = response.message ?? '';
  warning.value = response.warning ?? '';

  if (downloadPreUpdateImpressions.value) {
    await nextTick();
    downloadPreUpdateImpressionsCsv(response.preUpdateImpressions ?? []);
  }

  file.value = null;
};

//
// Measurements data table (server-side paginated)
//

type MeasurementsTableResponse = {
  data?: {
    measurements?: MeasurementRow[];
    permissions?: { tag: string }[];
  };
  pagination?: {
    total?: number;
    perPage?: number;
    currentPage?: number;
  };
};

type MeasurementsOptionsResponse = {
  data?: {
    providers?: SelectOption[];
    metrics?: SelectOption[];
    segments?: SelectOption[];
    venues?: SelectOption[];
    intervals?: SelectOption[];
    defaults?: { provider?: string; metric?: string; segment?: string; interval?: string };
    permissions?: { tag: string }[];
  };
};

const dt = ref();
const isTableLoading = ref(false);

// Uploader provider list excludes the synthetic "default" (calculated) option —
// data can only be imported under a real provider.
const uploadProviderOptions = computed(() => providerOptions.value.filter(provider => provider.code !== 'default'));

const valueHeader = computed(() => {
  const metricName = metricOptions.value.find(m => m.code === selectedMetric.value)?.name ?? 'Value';
  const intervalName = intervalOptions.value.find(i => i.code === selectedInterval.value)?.name ?? '';
  const suffix = selectedProvider.value === 'default'
    ? '(Default)'
    : (providerOptions.value.find(p => p.code === selectedProvider.value)?.name ?? '');
  return `${metricName} — ${selectedSegment.value} — ${intervalName} ${suffix}`.trim();
});

// Render the value cell. A null/undefined value (no data, or a non-additive metric
// with nothing stored at the selected interval) shows blank rather than "0".
const formatValue = (value: number | null | undefined) =>
  value === null || value === undefined
    ? ''
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });

// inv_number is a text search (Contains default, Starts With); inv_id and value
// are numeric searches (Contains default, Less Than, Greater Than).
const TEXT_MATCH_MODES = [
  { label: 'Contains', value: 'contains' },
  { label: 'Starts With', value: 'startsWith' },
];

const NUMERIC_MATCH_MODES = [
  { label: 'Contains', value: 'contains' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Greater Than', value: 'gt' },
];

const matchModeOptionsFor = (field: string) =>
  field === 'inv_number' ? TEXT_MATCH_MODES : NUMERIC_MATCH_MODES;

// Sentinel for "no resolved value" in the source-provider filter (mirrors the backend).
const EMPTY_PROVIDER = '__empty__';

// Source-provider filter options: each real provider by name, plus "(Empty)".
const providerFilterOptions = computed(() => [
  { label: '(Empty)', value: EMPTY_PROVIDER },
  ...providerOptions.value
    .filter(provider => provider.code !== 'default')
    .map(provider => ({ label: provider.name, value: provider.name })),
]);

const rowsPerPageOptions = computed(() => {
  const base = [100, 250, 500, 1000];
  const options = totalRecords.value > base[base.length - 1]
    ? [...base, totalRecords.value]
    : base;
  return [...new Set(options)].sort((a, b) => a - b);
});

const { getResults: fetchOptions } = useEndpoint<MeasurementsOptionsResponse>({
  type: 'GET',
  url: '/manage-measurements/options',
  successField: 'data',
  toast: { showOnSuccess: false },
});

const { getResults: fetchMeasurements } = useEndpoint<MeasurementsTableResponse>({
  type: 'POST',
  url: '/manage-measurements/table',
  successField: 'data',
  cancelPreviousRequestOnSubsequent: true,
  toast: { showOnSuccess: false },
});

// applyDefaults is true only on the first load; on a manual refresh the option
// lists are reloaded but the user's current selection is preserved.
const loadOptions = async (applyDefaults = false) => {
  const response = await fetchOptions();

  if (!response?.data) {
    return;
  }

  providerOptions.value = response.data.providers ?? [];
  metricOptions.value = response.data.metrics ?? [];
  segmentOptions.value = response.data.segments ?? [];
  venueOptions.value = response.data.venues ?? [];
  intervalOptions.value = response.data.intervals ?? [];
  adminToolsEnabled.value = (response.data.permissions ?? []).some(permission => permission.tag === 'hasAdminTools');

  if (applyDefaults) {
    selectedProvider.value = response.data.defaults?.provider ?? selectedProvider.value;
    selectedMetric.value = response.data.defaults?.metric ?? selectedMetric.value;
    selectedSegment.value = response.data.defaults?.segment ?? selectedSegment.value;
    selectedInterval.value = response.data.defaults?.interval ?? selectedInterval.value;
  }

  // Keep the uploader defaults (DEC / P0+) valid; fall back to the first available
  // option when the preferred default isn't present so the selects are never empty.
  if (!uploadProviderOptions.value.some(provider => provider.code === uploadProvider.value)) {
    uploadProvider.value = uploadProviderOptions.value[0]?.code ?? uploadProvider.value;
  }
  if (!segmentOptions.value.some(segment => segment.code === uploadSegment.value)) {
    uploadSegment.value = segmentOptions.value[0]?.code ?? uploadSegment.value;
  }
};

const loadMeasurements = async (page: number) => {
  isTableLoading.value = true;

  const response = await fetchMeasurements({
    page,
    pageSize: rowsPerPage.value,
    sortField: tableSortField.value,
    sortOrder: tableSortOrder.value,
    filters: tableFilters.value,
    provider: selectedProvider.value,
    metric: selectedMetric.value,
    segment: selectedSegment.value,
    interval: selectedInterval.value,
  });

  if (response) {
    measurements.value = response.data?.measurements ?? [];
    totalRecords.value = response.pagination?.total ?? 0;
    adminToolsEnabled.value = (response.data?.permissions ?? []).some(permission => permission.tag === 'hasAdminTools');
  }

  isTableLoading.value = false;
};

// Derive the 1-based page from the persisted paginator offset so the table
// resumes on the same page after a navigation round-trip.
const currentPage = () => Math.floor(firstRecord.value / rowsPerPage.value) + 1;

const onProfileChange = () => {
  firstRecord.value = 0;
  loadMeasurements(1);
};

const onPage = (event: DataTablePageEvent) => {
  rowsPerPage.value = event.rows;
  firstRecord.value = event.first;
  loadMeasurements(currentPage());
};

const onSort = (event: DataTableSortEvent) => {
  tableSortField.value = (event.sortField as string) ?? null;
  tableSortOrder.value = event.sortOrder ?? null;
  firstRecord.value = 0;
  loadMeasurements(1);
};

const FILTER_DEBOUNCE_MS = 400;
let filterDebounceTimer: number | undefined;

const onFilter = (event: DataTableFilterEvent) => {
  tableFilters.value = { ...(event.filters as typeof tableFilters.value) };
  firstRecord.value = 0;

  if (filterDebounceTimer) {
    window.clearTimeout(filterDebounceTimer);
  }

  filterDebounceTimer = window.setTimeout(() => loadMeasurements(1), FILTER_DEBOUNCE_MS);
};

const exportCsv = () => {
  dt.value.exportCSV();
};

//
// Admin: provider priority ordering
//

type ProviderPriorityResponse = {
  data?: { providers?: ProviderPriorityRow[] };
};


const { getResults: fetchProviderPriority, isLoading: isLoadingPriority } = useEndpoint<ProviderPriorityResponse>({
  type: 'GET',
  url: '/manage-measurements/providers',
  successField: 'data',
  toast: { showOnSuccess: false },
});

const { getResults: saveProviderPriorityRequest, isLoading: isSavingPriority } = useEndpoint<ProviderPriorityResponse>({
  type: 'POST',
  url: '/manage-measurements/providers/priority',
  successField: 'data',
  toast: { showOnSuccess: true, successMessage: 'Provider priority saved' },
});

const applyProviderPriority = (rows: ProviderPriorityRow[]) => {
  providerPriority.value = rows;
  originalProviderOrder.value = rows.map(row => row.id);
};

const providerOrderDirty = computed(() =>
  providerPriority.value.some((row, index) => row.id !== originalProviderOrder.value[index]),
);

const loadProviderPriority = async () => {
  const response = await fetchProviderPriority();

  if (response?.data?.providers) {
    applyProviderPriority(response.data.providers);
  }
};

const onProviderReorder = (event: { value: ProviderPriorityRow[] }) => {
  providerPriority.value = event.value;
};

const saveProviderPriority = async () => {
  if (!providerOrderDirty.value || isSavingPriority.value) {
    return;
  }

  const response = await saveProviderPriorityRequest<ProviderPriorityResponse, { providers: number[] }>({
    providers: providerPriority.value.map(row => row.id),
  });

  if (response?.data?.providers) {
    applyProviderPriority(response.data.providers);
  }
};

// Re-pull data on demand, keeping the current selection / filters / sort / page.
const refresh = async () => {
  await loadOptions(false);
  await loadMeasurements(currentPage());

  if (adminToolsEnabled.value) {
    await loadProviderPriority();
  }
};

onMounted(async () => {
  // Re-clicking the active "Manage Measurements" sidebar item dispatches this
  // event (see SidebarItemList.vue); registered before the initialized guard so
  // it stays wired up when returning to an already-loaded screen.
  window.addEventListener(CustomEventNames.measurements.reloadHome, refresh);

  // State lives in the Pinia store and survives this component being unmounted
  // when the user navigates away, so only fetch on the first visit. Use the
  // Refresh button to re-pull on demand.
  if (measurementsStore.initialized) {
    return;
  }

  await loadOptions(true);
  await loadMeasurements(currentPage());

  if (adminToolsEnabled.value) {
    await loadProviderPriority();
  }

  measurementsStore.initialized = true;
});

onBeforeUnmount(() => window.removeEventListener(CustomEventNames.measurements.reloadHome, refresh));

</script>

<template>
  <AppContainer>
    <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-2">
        <Avatar
          icon="pi pi-gauge"
          size="normal"
          shape="circle"
          style="background-color: var(--p-blue-100); color: var(--p-blue-500);"
        />
        <h1 class="text-lg">
          Manage Measurements
        </h1>
        <Badge :value="totalRecords" size="large" severity="secondary" />
      </div>
      <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
        <Button
          v-tooltip.left="'Refresh'"
          size="small"
          severity="secondary"
          class="shrink-0 whitespace-nowrap"
          :loading="isTableLoading"
          @click="refresh()"
        >
          <i class="pi pi-refresh" /> <span style="font-weight:700;">
            Refresh
          </span>
        </Button>
        <Button
          v-tooltip.left="'Export'"
          size="small"
          severity="secondary"
          class="shrink-0 whitespace-nowrap"
          @click="exportCsv()"
        >
          <i class="pi pi-file-export" /> <span style="font-weight:700;">
            Export
          </span>
        </Button>
      </div>
    </div>

    <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end">
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Provider
          <i
            v-tooltip="filterDescriptions.provider"
            class="pi pi-info-circle text-gray-500 cursor-pointer"
          />
        </label>
        <Select
          v-model="selectedProvider"
          :options="providerOptions"
          option-label="label"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Metric
          <i
            v-tooltip="filterDescriptions.metric"
            class="pi pi-info-circle text-gray-500 cursor-pointer"
          />
        </label>
        <Select
          v-model="selectedMetric"
          :options="metricOptions"
          option-label="name"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Audience
          <i
            v-tooltip="filterDescriptions.segment"
            class="pi pi-info-circle text-gray-500 cursor-pointer"
          />
        </label>
        <Select
          v-model="selectedSegment"
          :options="segmentOptions"
          option-label="name"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Interval
          <i
            v-tooltip="filterDescriptions.interval"
            class="pi pi-info-circle text-gray-500 cursor-pointer"
          />
        </label>
        <Select
          v-model="selectedInterval"
          :options="intervalOptions"
          option-label="name"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
    </div>

    <HeightOptimizer>
      <template #default="{ availableHeight }">
        <DataTable
          ref="dt"
          v-model:filters="tableFilters"
          lazy
          :value="measurements"
          :loading="isTableLoading"
          :total-records="totalRecords"
          :rows="rowsPerPage"
          :first="firstRecord"
          paginator
          :rows-per-page-options="rowsPerPageOptions"
          filter-display="row"
          data-key="inv_id"
          table-style="min-width: 50rem;"
          table-layout="fixed"
          scrollable
          :scroll-height="`calc(${availableHeight} - 60px)`"
          :virtual-scroller-options="{ itemSize: 50 }"
          reorderable-columns
          resizable-columns
          column-resize-mode="expand"
          row-hover
          :pt="{ bodyRow: { class: 'cursor-pointer' } }"
          @page="onPage"
          @sort="onSort"
          @filter="onFilter"
          @row-click="onRowClick"
        >
          <Column
            field="inv_number"
            header="Number"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('inv_number')"
          >
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="inv_type"
            header="Type"
            sortable
            :show-filter-menu="false"
          >
            <template #body="{ data }">
              {{ venueOptions.find(v => v.code === data.inv_type)?.name ?? data.inv_type ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <MultiSelect
                v-model="filterModel.value"
                :options="venueOptions"
                option-label="name"
                option-value="code"
                placeholder="All"
                show-clear
                class="p-column-filter"
                style="min-width: 10rem"
                @change="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="value"
            :header="valueHeader"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('value')"
          >
            <template #body="{ data }">
              {{ formatValue(data.value) }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            v-if="selectedProvider === 'default'"
            field="value_provider"
            header="Source Provider"
            sortable
            :show-filter-menu="false"
          >
            <template #body="{ data }">
              {{ data.value_provider ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <Select
                v-model="filterModel.value"
                :options="providerFilterOptions"
                option-label="label"
                option-value="value"
                placeholder="All"
                show-clear
                class="p-column-filter"
                style="min-width: 10rem"
                @change="filterCallback()"
              />
            </template>
          </Column>
        </DataTable>
      </template>
    </HeightOptimizer>

    <div v-if="adminToolsEnabled" class="pt-10">
      <Section :footer="false">
        <Typography class="pb-2" variant="section-heading">
          Admin Tools
        </Typography>

        <UserRoleGate :allow-for="['Technologist']">
          <div class="mb-4 max-w-4xl rounded-lg border p-3">
            <Typography class="pb-2" variant="section-subheading">
              Upload Impressions
            </Typography>
            <Typography class="pb-3" variant="form-label">
              Upload impressions via a csv, xls, or xlsx file with two columns: Face, Impressions.
              Face names must match the names as seen in CORE &gt; Inventory &gt; Faces in the REF column. Please remove any prefixes before attempting to upload.
              Choose the Provider, Audience, and Interval the impressions are reported for. The data is imported as a manual measurement owned by you.
            </Typography>

            <form @submit.prevent="uploadFile">
              <div class="mb-3">
                <Typography variant="form-label" for="file" class="mb-1 flex items-center gap-1">
                  Select File
                </Typography>
                <FileUpload
                  mode="basic"
                  accept=".csv,.xls,.xlsx"
                  choose-label="Choose File"
                  custom-upload
                  :choose-button-props="{ size: 'small' }"
                  @select="handleFileSelect"
                />
              </div>

              <div class="grid gap-3 md:grid-cols-2">
                <div>
                  <Typography variant="form-label" class="mb-1 flex items-center gap-1">
                    Metric
                  </Typography>
                  <InputText
                    model-value="Impressions"
                    readonly
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="uploadProvider" class="mb-1 flex items-center gap-1">
                    Provider
                  </Typography>
                  <Select
                    id="uploadProvider"
                    v-model="uploadProvider"
                    :options="uploadProviderOptions"
                    option-label="name"
                    option-value="code"
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="uploadSegment" class="mb-1 flex items-center gap-1">
                    Audience
                  </Typography>
                  <Select
                    id="uploadSegment"
                    v-model="uploadSegment"
                    :options="segmentOptions"
                    option-label="name"
                    option-value="code"
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="interval" class="mb-1 flex items-center gap-1">
                    Impressions Interval
                    <i
                      v-tooltip="impressionsIntervalTooltip"
                      class="pi pi-info-circle text-gray-500 cursor-pointer"
                    />
                  </Typography>
                  <Select
                    id="interval"
                    v-model="interval"
                    :options="uploadIntervalOptions"
                    option-label="name"
                    option-value="value"
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="headerRow" class="mb-1 flex items-center gap-1">
                    Header Row
                    <i
                      v-tooltip="headerRowTooltip"
                      class="pi pi-info-circle text-gray-500 cursor-pointer"
                    />
                  </Typography>
                  <InputNumber
                    v-model="headerRow"
                    input-id="headerRow"
                    :min="1"
                    :max-fraction-digits="0"
                    :use-grouping="false"
                    :allow-empty="false"
                    class="w-full"
                    input-class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="firstDataRow" class="mb-1 flex items-center gap-1">
                    First Data Row
                    <i
                      v-tooltip="firstDataRowTooltip"
                      class="pi pi-info-circle text-gray-500 cursor-pointer"
                    />
                  </Typography>
                  <InputNumber
                    v-model="firstDataRow"
                    input-id="firstDataRow"
                    :min="2"
                    :max-fraction-digits="0"
                    :use-grouping="false"
                    :allow-empty="false"
                    class="w-full"
                    input-class="w-full"
                  />
                </div>
              </div>

              <div class="mt-3 flex items-center gap-2">
                <Checkbox
                  v-model="downloadPreUpdateImpressions"
                  input-id="downloadPreUpdateImpressions"
                  binary
                />
                <Typography variant="form-label" for="downloadPreUpdateImpressions" class="flex items-center gap-1">
                  Download Pre-Update Impressions
                  <i
                    v-tooltip="downloadPreUpdateImpressionsTooltip"
                    class="pi pi-info-circle text-gray-500 cursor-pointer"
                  />
                </Typography>
              </div>

              <div class="flex flex-wrap items-center gap-4 pt-3">
                <Button
                  size="small"
                  type="submit"
                  class="flex items-center"
                  :disabled="!file || isUploading"
                  :loading="isUploading"
                >
                  <span class="font-bold">Upload File</span>
                </Button>
                <a
                  href="/docs/templates/upload-impressions-template.csv"
                  class="text-[var(--text-link-primary)] hover:underline"
                >
                  Download Template CSV
                </a>
                <a
                  href="/docs/templates/upload-impressions-template.xlsx"
                  class="text-[var(--text-link-primary)] hover:underline"
                >
                  Download Template XLSX
                </a>
              </div>

              <p v-if="message" class="mt-4 whitespace-pre-line text-green-600">
                {{ message }}
              </p>
              <p v-if="error" class="mt-4 whitespace-pre-line text-red-600">
                {{ error }}
              </p>
              <p v-if="warning" class="mt-4 whitespace-pre-line text-yellow-600">
                {{ warning }}
              </p>
            </form>
          </div>
        </UserRoleGate>

        <div class="mb-4 max-w-2xl rounded-lg border p-3">
          <Typography class="pb-2" variant="section-subheading">
            Provider Priority
          </Typography>
          <Typography class="pb-3" variant="form-label">
            Drag providers to set the read-resolution order. When a value is shown for the "Default (calculated)" provider,
            each data point uses the highest-ranked provider (top of this list) that has a non-zero value.
            Saving renumbers priorities to clean multiples of 100, leaving room to inject a provider between two ranks later.
          </Typography>

          <DataTable
            :value="providerPriority"
            :loading="isLoadingPriority"
            data-key="id"
            size="small"
            class="text-sm"
            @row-reorder="onProviderReorder"
          >
            <Column row-reorder header-style="width: 3rem" />
            <Column header="Rank" header-style="width: 4rem">
              <template #body="{ index }">
                {{ index + 1 }}
              </template>
            </Column>
            <Column field="name" header="Provider">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ data.name }}</span>
                  <Tag v-if="!data.isActive" value="Inactive" severity="secondary" />
                </div>
              </template>
            </Column>
            <Column field="code" header="Code" />
          </DataTable>

          <div class="flex items-center gap-3 pt-3">
            <Button
              size="small"
              :disabled="!providerOrderDirty || isSavingPriority"
              :loading="isSavingPriority"
              @click="saveProviderPriority"
            >
              <span class="font-bold">Save Priority</span>
            </Button>
            <span v-if="providerOrderDirty" class="text-sm text-yellow-600">Unsaved changes</span>
          </div>
        </div>
      </Section>
    </div>
    <RouterView />
  </AppContainer>
</template>

<style scoped>
.datatable-container {
  /* Ensure the container occupies full height */
  height: 100vh;
  display: flex;
  flex-direction: column;
}

:deep(.p-datatable-header) {
    background: none;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
